/**
 * @file useLiveSiteIssues.ts
 * @description «Без чего сайт работает сейчас» для «Сводки» кабинета
 * (phase19.16, SB125; партия SB120, этап 2 — uiux-sb120-stage2 §4,
 * db-design-sb120-stage2 §6; TD-407): `GET admin/site-view/live-issues →
 * { published, errors[] }`. Запрос уходит только при праве смотреть
 * черновик вида сайта (`enabled` — `canViewSiteDraft`), ответ свежий 60 с
 * (`live-issues-store.ts`) и только для своего кабинета (`contextKey`):
 * сменили магазин — запись чужая, запрос заново. Ошибка запроса — плашки
 * нет (не мешаем «Сводке»).
 */

import { useEffect, useSyncExternalStore } from "react";

import { siteView } from "../../lib/api-site-view";
import {
  liveIssuesEntry,
  liveIssuesFresh,
  liveIssuesGeneration,
  storeLiveIssues,
  subscribeLiveIssues,
  type LiveIssuesEntry,
} from "../../lib/site-view/live-issues-store";

/** Один запрос на вкладку: поколение кэша и кабинет, для которых он начат. */
let inflight: string | null = null;

function load(key: string): void {
  const started = liveIssuesGeneration();
  const id = `${started}\u0000${key}`;
  if (inflight === id) return;
  inflight = id;
  siteView
    .liveIssues()
    .then((data) => storeLiveIssues(started, key, data, false))
    .catch(() => storeLiveIssues(started, key, null, true))
    .finally(() => {
      if (inflight === id) inflight = null;
    });
}

/**
 * Запись кэша своего кабинета; `null` — нет права, нет кабинета, ещё
 * грузится или сброшено.
 */
export function useLiveSiteIssues(enabled: boolean, contextKey: string | null): LiveIssuesEntry | null {
  const entry = useSyncExternalStore(subscribeLiveIssues, liveIssuesEntry, () => null);
  useEffect(() => {
    if (enabled && contextKey && !liveIssuesFresh(contextKey)) load(contextKey);
    // Сброс кэша (entry → null) или смена кабинета — перечитать сразу, пока плашка на экране.
  }, [enabled, contextKey, entry]);
  return enabled && contextKey && entry?.key === contextKey ? entry : null;
}
